import { Box, Typography, Card, CardContent, useTheme, alpha, TextField } from '@mui/material';
import { useState, useEffect } from 'react';
import { genericFormProps } from '../signup.types';
import { roles, purposes } from './roleSelectionData';

interface RoleSelectionFormProps extends genericFormProps {
  formSubmitting: boolean;
  showSteps?: boolean;
}

const RoleSelectionForm: React.FC<RoleSelectionFormProps> = ({ values, setFieldValue, formSubmitting }) => {
  const theme = useTheme();
  const [otherOccupationText, setOtherOccupationText] = useState('');
  const [otherPurposeText, setOtherPurposeText] = useState('');
  const [showOtherOccupation, setShowOtherOccupation] = useState(false);
  const [showOtherPurpose, setShowOtherPurpose] = useState(false);

  // Initialize state from values if they don't match predefined roles
  useEffect(() => {
    if (values.occupation && !roles.find(r => r.label === values.occupation)) {
      setOtherOccupationText(values.occupation);
      setShowOtherOccupation(true);
    } else if (values.occupation === 'Other Professional') {
      setShowOtherOccupation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasOther = (values.usagePurpose || []).includes('Other');
    setShowOtherPurpose(hasOther);

    const otherPurposes = (values.usagePurpose || []).filter(
      purpose => purpose !== 'Other' && !purposes.find(p => p.label === purpose)
    );
    if (otherPurposes.length > 0) {
      setOtherPurposeText(otherPurposes.join(', '));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRoleSelect = (roleLabel: string) => {
    if (roleLabel === 'Other Professional') {
      setShowOtherOccupation(true);
      setFieldValue('occupation', roleLabel);
    } else {
      setShowOtherOccupation(false);
      setFieldValue('occupation', roleLabel);
      setOtherOccupationText('');
    }
  };

  const handlePurposeToggle = (purposeLabel: string) => {
    const currentPurposes = values.usagePurpose || [];
    const isSelected = currentPurposes.includes(purposeLabel);

    if (isSelected) {
      const filtered = currentPurposes.filter(label => label !== purposeLabel);
      setFieldValue('usagePurpose', filtered);
      if (purposeLabel === 'Other') {
        setShowOtherPurpose(false);
        // Remove custom text from array as well
        const cleanedPurposes = filtered.filter(p => purposes.find(purpose => purpose.label === p));
        setFieldValue('usagePurpose', cleanedPurposes);
        setOtherPurposeText('');
      }
    } else {
      setFieldValue('usagePurpose', [...currentPurposes, purposeLabel]);
      if (purposeLabel === 'Other') {
        setShowOtherPurpose(true);
      }
    }
  };

  const handleOtherOccupationChange = (text: string) => {
    setOtherOccupationText(text);
    // Update the occupation field with the custom text
    setFieldValue('occupation', text || 'Other Professional');
  };

  const handleOtherPurposeChange = (text: string) => {
    setOtherPurposeText(text);
    // Get all non-"Other" purposes and non-custom purposes
    const standardPurposes = (values.usagePurpose || []).filter(p =>
      p === 'Other' || purposes.find(purpose => purpose.label === p)
    );
    // Add the custom text if it's not empty
    if (text.trim()) {
      const withoutOther = standardPurposes.filter(p => p !== 'Other');
      setFieldValue('usagePurpose', [...withoutOther, 'Other', text.trim()]);
    } else {
      setFieldValue('usagePurpose', standardPurposes);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      {/* Page Title */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          Welcome to Soopra!
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Let's start by understanding who you are and how you plan to use your AI persona
        </Typography>
      </Box>

      {/* Role Selection Section */}
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          What best describes you?
        </Typography>
        <Typography
          sx={{
            color: 'primary.main',
            fontSize: 16,
            fontFamily: '"Merriweather Sans", sans-serif',
            fontWeight: 600,
            lineHeight: '20.8px',
            letterSpacing: '0.15px',
            mb: 2,
          }}
        >
          *Required
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 1.5,
            width: '100%',
          }}
        >
          {roles.map((role) => {
            const Icon = role.icon;
            const isSelected = values.occupation === role.label;

            return (
              <Card
                key={role.label}
                onClick={() => !formSubmitting && handleRoleSelect(role.label)}
                sx={{
                  cursor: formSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  border: '1px solid',
                  borderColor: isSelected ? role.color : 'divider',
                  bgcolor: isSelected ? alpha(role.color, 0.08) : 'background.paper',
                  boxShadow: 'none',
                  '&:hover': {
                    transform: formSubmitting ? 'none' : 'translateY(-2px)',
                    boxShadow: formSubmitting ? 'none' : 1,
                    borderColor: role.color,
                  },
                  opacity: formSubmitting ? 0.6 : 1,
                  width: '100%',
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    p: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 1.5 },
                    height: '100%',
                    minHeight: { xs: '100px', sm: '120px' },
                    '&:last-child': {
                      pb: { xs: 1.5, sm: 2 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 36, sm: 48 },
                      height: { xs: 36, sm: 48 },
                      borderRadius: '50%',
                      bgcolor: alpha(role.color, 0.15),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 1, sm: 1.5 },
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: { xs: 20, sm: 28 },
                        color: role.color,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isSelected ? 600 : 500,
                      color: 'text.primary',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      lineHeight: 1.3,
                      width: '100%',
                    }}
                  >
                    {role.label}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Other Professional Text Field */}
        {showOtherOccupation && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Please specify your profession"
              placeholder="e.g., Architect, Consultant, Designer..."
              value={otherOccupationText}
              onChange={(e) => handleOtherOccupationChange(e.target.value)}
              disabled={formSubmitting}
              size="small"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Purpose Section */}
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          What brings you to Soopra?
        </Typography>
        <Typography
          sx={{
            color: 'primary.main',
            fontSize: 16,
            fontFamily: '"Merriweather Sans", sans-serif',
            fontWeight: 600,
            lineHeight: '20.8px',
            letterSpacing: '0.15px',
            mb: 0.5,
          }}
        >
          *Required
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mb: 2,
            display: 'block',
          }}
        >
          Select all that apply
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {purposes.map((purpose) => {
            const Icon = purpose.icon;
            const isSelected = (values.usagePurpose || []).includes(purpose.label);
            return (
              <Card
                key={purpose.label}
                onClick={() => !formSubmitting && handlePurposeToggle(purpose.label)}
                sx={{
                  cursor: formSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  border: '1px solid',
                  borderColor: isSelected ? theme.palette.primary.main : 'divider',
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: formSubmitting ? 'none' : 1,
                  },
                  opacity: formSubmitting ? 0.6 : 1,
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: { xs: 1.5, sm: 2 },
                    '&:last-child': {
                      pb: { xs: 1.5, sm: 2 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                      borderRadius: '50%',
                      bgcolor: isSelected
                        ? alpha(theme.palette.text.primary, 0.1)
                        : alpha(theme.palette.text.primary, 0.05),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: { xs: 18, sm: 22 },
                        color: 'text.primary',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isSelected ? 600 : 400,
                      color: 'text.primary',
                      fontSize: { xs: '0.875rem', sm: '0.875rem' },
                    }}
                  >
                    {purpose.label}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Other Usage Purpose Text Field */}
        {showOtherPurpose && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Please specify your purpose"
              placeholder="e.g., Research, Entertainment, Personal assistant..."
              value={otherPurposeText}
              onChange={(e) => handleOtherPurposeChange(e.target.value)}
              disabled={formSubmitting}
              size="small"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                },
              }}
            />
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default RoleSelectionForm;